import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  LayoutGrid,
  ShieldCheck,
  Target,
  Coins,
  Activity,
  ScanSearch,
  CheckCircle2,
  CircleDot,
  AlertTriangle,
  GaugeCircle,
  Layers,
  ListChecks,
  Sparkles,
} from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";

const SITE = "https://xauproacademy.com";
const PAGE_URL = `${SITE}/platform/execution-desk`;
const TITLE = "Trading Journal for Gold Traders | Execution Desk by XAU Pro";
const DESCRIPTION =
  "Execution Desk is the trading journal for gold traders and funded accounts. Multi-account control, rule-based grading, FTMO and prop firm guardrails, StructureLens chart review, optional Orion signal match.";

const PROMOTED_QUERIES = [
  "trading journal",
  "gold trading journal",
  "prop firm trading journal",
  "FTMO trading journal",
  "multi account trading journal",
  "trade review software for gold",
];

export const Route = createFileRoute("/platform/execution-desk")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "keywords", content: PROMOTED_QUERIES.join(", ") },
      { property: "og:title", content: "Trading Journal for Gold Traders | Execution Desk" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:title", content: "Trading Journal for Gold Traders | Execution Desk" },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Execution Desk",
          alternateName: "Execution Desk Trading Journal",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          description: DESCRIPTION,
          url: PAGE_URL,
          featureList: [
            "Trading journal for XAUUSD (Gold)",
            "Multi-account management (personal and funded)",
            "Funded challenge control room (daily and max drawdown awareness)",
            "Rule-based execution grading against the XAU Pro doctrine",
            "Per-account rule binding (prop firm and broker rules)",
            "Gold-specific session and segment performance",
            "StructureLens chart review handoff",
            "Orion signal match when a signal existed at trade time",
            "Private execution archive, observational tone, no gamification",
          ],
          audience: {
            "@type": "Audience",
            audienceType:
              "Gold traders, funded account traders, FTMO and prop firm traders, SMC practitioners",
          },
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            description: "Free account access. Members area unlocks the full Execution Desk.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Platform", item: `${SITE}/platform` },
            { "@type": "ListItem", position: 3, name: "Execution Desk", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  component: ExecutionDeskPage,
});

function MicroLabel({ children, tone = "muted" }: { children: React.ReactNode; tone?: "muted" | "gold" }) {
  const cls =
    tone === "gold"
      ? "text-gold"
      : "text-muted-foreground";
  return (
    <div className={`font-mono text-[10px] uppercase tracking-[0.28em] ${cls}`}>
      {children}
    </div>
  );
}

function ExecutionDeskPage() {
  const { user } = useAuth();
  const primaryTo = user ? "/dashboard" : "/auth";
  const primaryLabel = user ? "Open XAU Terminal™" : "Get early access";

  return (
    <Layout>
      {/* 1. Hero */}
      <section className="overflow-x-clip border-b border-border/60 bg-background">
        <div className="mx-auto w-full max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid min-w-0 items-start gap-12 lg:grid-cols-[1.1fr_1fr]">
            <div className="min-w-0">
              <MicroLabel tone="gold">EXECUTION DESK™ · Capability 02 · Early access</MicroLabel>
              <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-foreground lg:text-6xl">
                Trading Journal for Gold Traders.
              </h1>
              <p className="mt-4 max-w-xl text-lg font-medium leading-snug text-foreground/90 lg:text-xl">
                Manage personal accounts, funded challenges, and funded accounts from one control room.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  "Track drawdown",
                  "Monitor consistency",
                  "Review execution",
                  "Stay inside challenge rules",
                ].map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center rounded-sm border border-border/60 bg-background/60 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/80"
                  >
                    {chip}
                  </span>
                ))}
              </div>
              <div className="mt-5 border-l-2 border-gold/60 pl-4">
                <p className="text-sm leading-relaxed text-foreground/90 lg:text-base">
                  Most funded traders do not fail because of strategy.
                  They fail because they lose track of rules, drawdown limits,
                  and multiple accounts at the same time.
                </p>
              </div>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-muted-foreground lg:text-base">
                Built for XAUUSD traders managing personal accounts, funded
                challenges, and funded accounts side by side. Rule-based
                grading, StructureLens chart review, and an optional Orion
                signal match when one existed at trade time.
              </p>
              <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <Button asChild variant="gold" size="lg" className="justify-center px-6 sm:px-8">
                  <Link
                    to={primaryTo}
                    {...(!user ? { search: { tab: "signup", interest: "execution_desk" } as any } : {})}
                  >
                    <span className="leading-tight">{primaryLabel}</span>
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <a
                  href="#funded-control"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-gold"
                >
                  See the funded control room <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Built for funded traders · Multiple accounts supported · No card required
              </div>
            </div>

            {/* Hero artifact: account switcher mock */}
            <div className="min-w-0">
              <HeroAccountSwitcher />
            </div>
          </div>
        </div>
      </section>

      {/* 2. Multi-Account Control */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>The journal that breaks for funded traders</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Most traders do not fail because of one bad trade. They fail because three accounts, two prop firms, and one personal broker live in five different spreadsheets.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Generic journals assume one trader, one account. The funded trader
            lives in a different reality: an FTMO challenge, a funded live
            account, a backup with another firm, plus a personal broker. Each
            with its own rules, its own drawdown, its own daily reset. One desk
            brings them together.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: LayoutGrid,
                title: "All accounts, one surface",
                body: "Switch between personal and funded accounts without leaving the journal. Same execution rules, account-specific guardrails.",
              },
              {
                icon: Layers,
                title: "Account is the entity, not the trade",
                body: "Trades roll up to the account they were taken on. Daily drawdown, max drawdown, and rule compliance are tracked per account, not as one global blur.",
              },
              {
                icon: Target,
                title: "Per-account rule binding",
                body: "Bind FTMO, The5ers, or your broker's rules to a specific account. The journal reads each trade against the rules that apply to it.",
              },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="rounded-xl border border-border/60 bg-background/40 p-6"
                >
                  <Icon className="h-5 w-5 text-gold" />
                  <div className="mt-4 font-display text-lg font-semibold text-foreground">
                    {c.title}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {c.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Funded Challenge Control Room */}
      <section id="funded-control" className="border-b border-border/60 scroll-mt-20">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>Funded challenge control room</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            The dashboard a funded trader actually needs on day one.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Most prop firm failures are not strategy failures. They are
            drawdown-rule failures. Execution Desk surfaces the numbers that end
            challenges before they end them.
          </p>

          <div className="mt-10 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <FundedControlMock />
            <div className="grid gap-4">
              {[
                {
                  icon: GaugeCircle,
                  title: "Daily drawdown headroom",
                  body: "How much you can still lose today before the account is breached. Updated per trade, per account.",
                },
                {
                  icon: ShieldCheck,
                  title: "Max drawdown headroom",
                  body: "The hard ceiling. Trailing or static, configured per challenge type.",
                },
                {
                  icon: ListChecks,
                  title: "Rule compliance log",
                  body: "Every trade tagged against the rules that applied at the time it was taken. Lot caps, news windows, weekend holds.",
                },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.title}
                    className="rounded-xl border border-border/60 bg-background/40 p-5"
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="mt-0.5 h-5 w-5 text-gold" />
                      <div>
                        <div className="font-display text-base font-semibold text-foreground">
                          {c.title}
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {c.body}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Execution Quality (rule-based grading) */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>Execution quality, not vibes</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Every trade reviewed against the rules that fired, not a generic checklist.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Execution Desk grades each trade against three concrete sources of
            truth. No mood ratings, no streaks, no stars for showing up.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            <ChecklistCard
              icon={ShieldCheck}
              eyebrow="Source 01"
              title="Your account rules"
              items={[
                "Risk per trade respected",
                "Daily and max drawdown within limits",
                "Lot caps and instrument scope honored",
                "News and weekend window compliance",
              ]}
            />
            <ChecklistCard
              icon={CheckCircle2}
              eyebrow="Source 02"
              title="The doctrine that fired"
              items={[
                "Liquidity interaction present",
                "Displacement confirmed",
                "POI quality acceptable",
                "Session and HTF alignment",
              ]}
            />
            <ChecklistCard
              icon={ScanSearch}
              eyebrow="Source 03"
              title="The Orion signal in play"
              items={[
                "Matched to an Orion signal when one existed",
                "Direction agreement logged",
                "Grade carried over for review",
                "Clearly marked when no signal was live",
              ]}
            />
          </div>

          <p className="mt-8 max-w-2xl text-sm italic text-muted-foreground">
            Grades come from rule satisfaction, not from how the trade felt.
            Winners can grade poorly. Losers can grade well. That is the point.
          </p>
        </div>
      </section>

      {/* 5. Gold-Specific Performance */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>Gold-specific performance</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Performance sliced the way XAUUSD actually moves.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            A blended win rate hides where the edge actually lives. Execution
            Desk segments performance by the variables that matter for gold.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "By session",
                body: "London open, NY open, London / NY overlap, Asia. Where your edge concentrates, where it disappears.",
              },
              {
                label: "By doctrine setup",
                body: "Liquidity sweep, OB continuation, FVG rebalance, BOS continuation. Per setup type, not as one bucket.",
              },
              {
                label: "By account type",
                body: "Personal vs funded. Behavior under drawdown pressure is rarely the same as behavior on a personal account.",
              },
              {
                label: "With or without Orion match",
                body: "When an Orion signal existed at trade time, results are split: matched vs unmatched. Honest segmentation.",
              },
            ].map((c) => (
              <div
                key={c.label}
                className="rounded-xl border border-border/60 bg-background/40 p-5"
              >
                <Coins className="h-5 w-5 text-gold" />
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
                  {c.label}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Orion Context (current capabilities only) */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>Orion context, honestly scoped</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Where an Orion signal existed, your trade carries the reference.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Orion does not publish continuously. It publishes when doctrine is
            satisfied. The desk treats that honestly.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gold/40 bg-background/40 p-6">
              <CheckCircle2 className="h-5 w-5 text-gold" />
              <div className="mt-4 font-display text-lg font-semibold text-foreground">
                When an Orion signal was live
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>Signal grade attached to the trade</li>
                <li>Direction agreement or disagreement logged</li>
                <li>Setup category carried over</li>
                <li>Reviewable inside the trade record</li>
              </ul>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/40 p-6">
              <CircleDot className="h-5 w-5 text-muted-foreground" />
              <div className="mt-4 font-display text-lg font-semibold text-foreground">
                When no signal was live
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>Trade is graded against doctrine and your account rules</li>
                <li>The journal does not invent a comparison that did not exist</li>
                <li>Marked clearly as discretionary, not Orion-matched</li>
                <li>Fully reviewable inside StructureLens</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Generic Journal vs Execution Desk */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>Generic journal vs Execution Desk</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            Built for the trader managing real account constraints.
          </h2>

          <div className="mt-10 overflow-x-auto rounded-xl border border-border/60 bg-background/40">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  <th className="px-4 py-3 font-mono font-normal">Dimension</th>
                  <th className="px-4 py-3 font-mono font-normal">Generic journal</th>
                  <th className="px-4 py-3 font-mono font-normal text-gold">Execution Desk</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {[
                  ["Account model", "One blended account", "Per-account, personal and funded side by side"],
                  ["Funded rules", "Manual notes", "Bound per account, checked per trade"],
                  ["Drawdown awareness", "Equity curve only", "Daily and max drawdown headroom, live"],
                  ["Grading", "Subjective tags", "Rule-based against doctrine and account rules"],
                  ["Gold specificity", "Symbol agnostic", "Session, setup, and segment built for XAUUSD"],
                  ["Chart review", "Screenshot library", "StructureLens reading attached to the trade"],
                  ["Signal context", "Out of scope", "Orion match when a signal existed at trade time"],
                ].map(([dim, generic, ed]) => (
                  <tr key={dim} className="text-foreground/85">
                    <td className="px-4 py-3 font-medium text-foreground">{dim}</td>
                    <td className="px-4 py-3 text-muted-foreground">{generic}</td>
                    <td className="px-4 py-3 text-foreground">{ed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 8. StructureLens Handoff */}
      <section className="border-b border-border/60 bg-slate-deep">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <div className="grid items-start gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <MicroLabel>StructureLens handoff</MicroLabel>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
                One click from a trade to an institutional chart reading.
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
                Attach the chart you traded. Send it to StructureLens. Receive a
                fixed-schema reading: structure, liquidity, displacement, POI,
                risk, grade. The reading lives on the trade, permanently.
              </p>
              <div className="mt-6">
                <Link
                  to="/platform/structurelens"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:underline"
                >
                  How StructureLens reads a chart <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/40 p-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Trade record · attached reading
              </div>
              <div className="mt-3 grid gap-2 font-mono text-xs text-foreground/85">
                <Row k="Bias" v="Bearish (H4)" />
                <Row k="Structure" v="BOS confirmed M15" />
                <Row k="Liquidity" v="London BSL swept" />
                <Row k="POI" v="H1 OB, unmitigated" />
                <Row k="Risk" v="Tight invalidation, 1:3 RR" />
                <Row k="Grade" v="A" tone="gold" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Honest Scope */}
      <section className="border-b border-border/60">
        <div className="container mx-auto max-w-6xl px-4 py-16 lg:py-20">
          <MicroLabel>Honest scope</MicroLabel>
          <h2 className="mt-2 max-w-3xl font-display text-2xl font-semibold tracking-tight text-foreground lg:text-3xl">
            What the desk does today, and what it deliberately does not.
          </h2>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gold/40 bg-background/40 p-6">
              <CheckCircle2 className="h-5 w-5 text-gold" />
              <div className="mt-4 font-display text-lg font-semibold text-foreground">
                What it does
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>Logs trades across multiple personal and funded accounts</li>
                <li>Tracks daily and max drawdown headroom per account</li>
                <li>Grades execution against doctrine and account rules</li>
                <li>Segments performance by session, setup, and account type</li>
                <li>Attaches a StructureLens reading to any trade</li>
                <li>Attaches the Orion signal when one was live at trade time</li>
              </ul>
            </div>
            <div className="rounded-xl border border-border/60 bg-background/40 p-6">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <div className="mt-4 font-display text-lg font-semibold text-foreground">
                What it does not
              </div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/85">
                <li>No motivational streaks, XP, or badges</li>
                <li>No social feed or public trade sharing</li>
                <li>No automated execution to your broker</li>
                <li>No backfilled Orion bias for trades from months ago</li>
                <li>No signal generation. That is Orion's job, not the journal's</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 10. CTA */}
      <section className="bg-slate-deep">
        <div className="container mx-auto max-w-4xl px-4 py-20 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold-muted/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            <Sparkles className="h-3.5 w-3.5" /> Early access
          </div>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-foreground lg:text-4xl">
            Bring your accounts onto one desk.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground">
            Execution Desk is opening to members first. Personal accounts,
            funded challenges, and live funded accounts on one operational
            surface, with the doctrine that grades every trade.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild variant="gold" size="lg">
              <Link
                to={primaryTo}
                {...(!user ? { search: { tab: "signup", interest: "execution_desk" } as any } : {})}
              >
                {primaryLabel} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/platform">Back to Platform</Link>
            </Button>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="rounded-sm border border-border/60 bg-background/40 px-2 py-1">No card</span>
            <span className="rounded-sm border border-border/60 bg-background/40 px-2 py-1">Multiple accounts supported</span>
            <span className="rounded-sm border border-border/60 bg-background/40 px-2 py-1">Cancel anytime</span>
          </div>
        </div>
      </section>
    </Layout>
  );
}

/* ---------- helpers ---------- */

function Row({ k, v, tone }: { k: string; v: string; tone?: "gold" }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-1.5 last:border-b-0 last:pb-0">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{k}</span>
      <span className={tone === "gold" ? "text-gold" : "text-foreground"}>{v}</span>
    </div>
  );
}

function ChecklistCard({
  icon: Icon,
  eyebrow,
  title,
  items,
}: {
  icon: typeof ShieldCheck;
  eyebrow: string;
  title: string;
  items: string[];
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/40 p-6">
      <Icon className="h-5 w-5 text-gold" />
      <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {eyebrow}
      </div>
      <div className="mt-1 font-display text-lg font-semibold text-foreground">
        {title}
      </div>
      <ul className="mt-3 space-y-2 text-sm text-foreground/85">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gold/70" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function HeroAccountSwitcher() {
  const tabs = [
    { label: "Personal", type: "PERSONAL" },
    { label: "FTMO 100K", type: "CHALLENGE", active: true },
    { label: "TopTier 50K", type: "FUNDED" },
  ];
  const metrics = [
    { k: "Target Progress", v: "73%", sub: "$7,300 / $10,000", pct: 73, tone: "gold" as const },
    { k: "Daily Drawdown", v: "6.1% / 10%", sub: "$610 used today", pct: 61, tone: "warn" as const },
    { k: "Consistency", v: "38%", sub: "Best day vs total profit", pct: 38, tone: "muted" as const },
    { k: "Days Remaining", v: "14", sub: "of 30 day window", pct: 53, tone: "muted" as const },
  ];
  return (
    <div className="rounded-2xl border border-border/60 bg-background/60 p-5 shadow-xl">
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
          Execution Desk · Control Room
        </div>
        <Activity className="h-4 w-4 text-gold" />
      </div>

      {/* Account tabs */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <div
            key={t.label}
            className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] ${
              t.active
                ? "border-gold/60 bg-gold-muted/20 text-foreground"
                : "border-border/60 bg-background/40 text-muted-foreground"
            }`}
          >
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] opacity-70">
              {t.type}
            </span>
            <span className="font-medium">{t.label}</span>
          </div>
        ))}
      </div>

      {/* Focused account header */}
      <div className="mt-4 rounded-lg border border-gold/40 bg-gold-muted/10 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-foreground">
              FTMO 100K · Phase 1
            </div>
            <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Challenge clock active
            </div>
          </div>
          <span className="rounded-sm border border-gold/40 bg-background/40 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
            Live
          </span>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        {metrics.map((m) => (
          <div
            key={m.k}
            className="rounded-lg border border-border/60 bg-background/40 px-3 py-2.5"
          >
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
              {m.k}
            </div>
            <div
              className={`mt-1 font-display text-lg font-semibold ${
                m.tone === "gold"
                  ? "text-gold"
                  : m.tone === "warn"
                  ? "text-amber-300"
                  : "text-foreground"
              }`}
            >
              {m.v}
            </div>
            <div className="mt-0.5 text-[10px] text-muted-foreground">{m.sub}</div>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-border/40">
              <div
                className={`h-full ${
                  m.tone === "gold"
                    ? "bg-gold"
                    : m.tone === "warn"
                    ? "bg-amber-400"
                    : "bg-foreground/50"
                }`}
                style={{ width: `${m.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer status */}
      <div className="mt-4 flex items-center justify-between border-t border-border/60 pt-3">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">
          <ShieldCheck className="h-3.5 w-3.5" />
          Rule breaches · 0
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          News window · clear
        </div>
      </div>
    </div>
  );
}

function FundedControlMock() {
  return (
    <div className="rounded-2xl border border-border/60 bg-background/40 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            FTMO · 100K · Phase 1
          </div>
          <div className="mt-1 font-display text-lg font-semibold text-foreground">
            Account control
          </div>
        </div>
        <ShieldCheck className="h-5 w-5 text-gold" />
      </div>

      <div className="mt-6 space-y-5">
        <Gauge label="Daily drawdown" used={1240} limit={5000} unit="$" />
        <Gauge label="Max drawdown" used={3850} limit={10000} unit="$" tone="warn" />
        <Gauge label="Profit target" used={6720} limit={10000} unit="$" tone="gold" />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 border-t border-border/60 pt-5">
        <Stat k="Trading days" v="6 / 4 min" />
        <Stat k="Rule breaches" v="0" tone="gold" />
        <Stat k="Open risk" v="0.40%" />
        <Stat k="News window" v="Clear" />
      </div>
    </div>
  );
}

function Gauge({
  label,
  used,
  limit,
  unit,
  tone = "default",
}: {
  label: string;
  used: number;
  limit: number;
  unit?: string;
  tone?: "default" | "warn" | "gold";
}) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const bar =
    tone === "gold"
      ? "bg-gold"
      : tone === "warn"
      ? "bg-amber-400"
      : "bg-foreground/70";
  return (
    <div>
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <span>{label}</span>
        <span className="text-foreground/80">
          {unit}
          {used.toLocaleString()} / {unit}
          {limit.toLocaleString()}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border/60">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Stat({ k, v, tone }: { k: string; v: string; tone?: "gold" }) {
  return (
    <div className="rounded-md border border-border/60 bg-background/60 px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
        {k}
      </div>
      <div className={`mt-1 font-mono text-sm ${tone === "gold" ? "text-gold" : "text-foreground"}`}>
        {v}
      </div>
    </div>
  );
}
